import { useCallback, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import type {
  FilterConfig,
  FilterState,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'
import { createDefaultFilterState } from '@/utilities/types/shared-filter'

import { UserFilterField } from '../filter-user/UserFilterField'

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

export interface FilterAreaControllerModalProps<T = any> {
  open: boolean
  onClose: () => void
  config: FilterConfig<T>
  onApplyFilter: (filters: FilterState<T>) => void
  initialFilters: FilterState<T>
  tab?: string
  optimizedDateFilterAreaControllers: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
  handleReset: () => void
}

export function FilterAreaControllerModal<T>({
  open,
  onClose,
  config,
  onApplyFilter,
  initialFilters,
  tab,
  optimizedDateFilterAreaControllers,
  handleAlertMessage,
  handleReset,
}: FilterAreaControllerModalProps<T>) {
  const [currentFilters, setCurrentFilters] = useState(() => ({
    ...createDefaultFilterState<T>(),
    ...initialFilters,
  }))

  const handleUpdateFilter = useCallback((field: string, value: string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      regularFilters: {
        ...prev.regularFilters,
        [field]: value,
      },
    }))
  }, [])

  const handleDateRangeChange = useCallback(
    ({ field, range }: UpdateDateRangeParams) => {
      setCurrentFilters((prev) => ({
        ...prev,
        dateRanges: {
          ...prev.dateRanges,
          [field]: range,
        },
      }))
    },
    [],
  )

  const handleApply = useCallback(() => {
    const { createdDate, modifiedDate } = currentFilters.dateRanges

    const startCreatedDate = createdDate.start != null || ''
    const endCreatedDate = createdDate.end != null || ''
    const startModifiedDate = modifiedDate.start != null || ''
    const endModifiedDate = modifiedDate.end != null || ''

    // Validation
    if (
      createdDate?.start &&
      createdDate?.end &&
      createdDate.start > createdDate.end
    ) {
      handleAlertMessage(
        'Created date start cannot be greater than end date.',
        'error',
      )
      return
    }

    if (
      modifiedDate?.start &&
      modifiedDate?.end &&
      modifiedDate.start > modifiedDate.end
    ) {
      handleAlertMessage(
        'Modified date start cannot be greater than end date.',
        'error',
      )
      handleAlertMessage(
        'Modified date start cannot be greater than end date.',
        'error',
      )
      return
    }

    if (
      createdDate?.start &&
      modifiedDate?.start &&
      modifiedDate.start < createdDate.start
    ) {
      handleAlertMessage(
        'Modified date cannot be earlier than created date.',
        'error',
      )
      return
    }

    // ✅ Trigger filter
    optimizedDateFilterAreaControllers(
      startCreatedDate,
      endCreatedDate,
      startModifiedDate,
      endModifiedDate,
    )

    onApplyFilter(currentFilters)
    onClose()
  }, [
    currentFilters,
    onApplyFilter,
    onClose,
    handleAlertMessage,
    optimizedDateFilterAreaControllers,
  ])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: '400px', p: 2 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Typography variant="h6" fontFamily="Gabarito">
          Filter By:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ mt: 2 }}>
            {config.fields.map((fieldConfig) => (
              <UserFilterField
                key={fieldConfig.field}
                config={fieldConfig}
                filters={currentFilters}
                onFilterChange={handleUpdateFilter}
                onDateRangeChange={handleDateRangeChange}
                tab={tab}
              />
            ))}
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ p: 0, mt: 2 }}>
        <Button
          onClick={handleReset}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontFamily: 'Gabarito',
          }}
        >
          Reset Filter
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: 'secondary.main',
            textTransform: 'none',
            fontFamily: 'Gabarito',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  )
}
